import { translate } from "app/filters/i18nFilter";
import { isEmpty, some } from "lodash";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";
import useSignalFiltersState from "app/pages/sales-intelligence/sub-modules/signals/hooks/useFiltersState";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import {
    getOptionsFromArray,
    getValuesFromArray,
} from "app/pages/sales-intelligence/sub-modules/signals/utils/filterValue";
import { useMemo } from "react";

import type AdNetworkSignalFilters from "../../../../../sub-modules/signals/models/ad-network/AdNetworkSignalFilters";
import type SignalAdNetwork from "../../../../../sub-modules/signals/models/ad-network/SignalAdNetwork";
import SignalAdNetworksFilterTracked from "../../../containers/SignalAdNetworksFilter/SignalAdNetworksFilterTracked";
import SignalDropdownLabel from "../../shared/SignalsDropdownLabel/SignalDropdownLabel";
import {
    StyledFiltersViewContent,
    StyledFiltersViewInner,
    StyledFiltersWrapper,
    StyledNotificationWrapper,
} from "../../styles";
import type SignalTemplate from "app/pages/sales-intelligence/sub-modules/signals/models/templates/SignalTemplate";

type AdNetworkSignalFiltersViewProps = SignalFiltersViewProps<AdNetworkSignalFilters> & {
    adNetworks: readonly SignalAdNetwork[];
};

const AdNetworkSignalFiltersView = (props: AdNetworkSignalFiltersViewProps) => {
    const hydratedFilters = useMemo<Partial<AdNetworkSignalFilters>>(() => {
        const adNetworksValue = getOptionsFromArray(props.filters.adNetworkIds, props.adNetworks);

        return {
            ...props.filters,
            adNetworkIds: adNetworksValue,
        };
    }, [props.filters, props.adNetworks]);

    const { currentFilters, setCurrentFilters } = useSignalFiltersState(hydratedFilters, handleFiltersChange);

    function getSubmitFiltersValue(): Partial<AdNetworkSignalFilters> {
        const { adNetworks } = props;
        const adNetworksValue = getValuesFromArray(currentFilters.adNetworkIds, adNetworks);

        return {
            action: currentFilters.action,
            adNetworkIds: adNetworksValue,
        };
    }

    function handleAdNetworkChange(adNetworkIds: readonly number[]) {
        setCurrentFilters({
            ...currentFilters,
            adNetworkIds,
        });
    }

    function handleFiltersChange(filters: Readonly<AdNetworkSignalFilters>) {
        props.onFiltersChange?.({
            ...filters,
            adNetworkIds: getValuesFromArray(filters.adNetworkIds, props.adNetworks),
        });
    }

    function handleSignalSubmit() {
        props.onFiltersSubmit(getSubmitFiltersValue());
    }

    function onTemplateSelect(option: Readonly<SignalTemplate<AdNetworkSignalFilters>>) {
        setCurrentFilters((prevFilters) => ({ ...prevFilters, action: option.filters.action }));
    }

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <SignalDropdownLabel text={translate("si.signals.management.label.websites")}>
                    {props.renderTemplateView({
                        onSelect: onTemplateSelect,
                    })}
                </SignalDropdownLabel>

                <StyledFiltersWrapper
                    alignItems="center"
                    gap="16px"
                >
                    <SignalAdNetworksFilterTracked
                        filterKey="adNetworkIds"
                        isActive={props.isActive}
                        signalAlias={props.signalAlias}
                        selectedValue={currentFilters.adNetworkIds}
                        options={props.adNetworks}
                        onValueChange={handleAdNetworkChange}
                    />
                </StyledFiltersWrapper>

                {props.notificationView ? <StyledNotificationWrapper>{props.notificationView}</StyledNotificationWrapper> : null}
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {props.renderSubmit({
                    isDisabled: some(currentFilters, isEmpty),
                    onSubmit: handleSignalSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
}

export default AdNetworkSignalFiltersView;
